import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, Mail, FileText, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

/**
 * SuperAdmin-only widget: shows traffic to the /api/partner/diet feed.
 * Data source: GET /api/admin/partner-diet/stats
 *
 * Renders:
 *   - Headline counts (24h / 7d / 30d / all-time)
 *   - Mini bar chart of the last 30 days
 *   - Top 10 sister-app sources by hit count
 */
function PartnerDietAnalyticsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [docBusy, setDocBusy] = useState(false);

  // Fetch the AIM integration doc once and either copy to clipboard or
  // download as a .md file. Source of truth is /app/docs on the backend so
  // the doc stays in sync without a redeploy.
  const fetchDoc = async () => {
    const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/partner-diet/integration-doc`);
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || `HTTP ${res.status}`);
    }
    return await res.text();
  };

  const copyDoc = async () => {
    setDocBusy(true);
    try {
      const md = await fetchDoc();
      await navigator.clipboard.writeText(md);
      toast.success('Integration doc copied: paste into email/Slack for the AIM team');
    } catch (e) {
      toast.error(e.message || 'Copy failed');
    } finally {
      setDocBusy(false);
    }
  };

  const downloadDoc = async () => {
    setDocBusy(true);
    try {
      const md = await fetchDoc();
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'PARTNER_DIET_API_FOR_AIM.md';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast.success('Downloaded PARTNER_DIET_API_FOR_AIM.md');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setDocBusy(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/partner-diet/send-daily-digest-now`, {
        method: 'POST',
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Digest send failed');
        return;
      }
      const j = await res.json();
      const recipients = (j.recipients || []).join(', ');
      toast.success(`Digest sent to ${recipients || 'superadmin'}`);
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/partner-diet/stats`);
        if (!res.ok) {
          if (alive) setErr(`HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        if (alive) setStats(data);
      } catch (e) {
        if (alive) setErr('Network error');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4" data-testid="partner-analytics-loading">
        <div className="text-xs text-zinc-500">Loading partner-diet analytics…</div>
      </div>
    );
  }

  if (err || !stats) {
    return (
      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4" data-testid="partner-analytics-error">
        <div className="text-xs text-zinc-500">
          Partner-diet analytics unavailable{err ? ` (${err})` : ''}.
        </div>
      </div>
    );
  }

  const byDay = stats.by_day || [];
  const maxCount = Math.max(1, ...byDay.map(d => d.count));

  // Compute freshness signal: has axiom-aim hit us in the last 24h?
  // Mirrors the backend dead-hand watchdog so SuperAdmin sees a warning
  // banner in the widget even before the 24h email fires.
  const sources = stats.by_source || [];
  const aimSource = sources.find(s => (s.source || '').toLowerCase() === 'axiom-aim');
  const aimTotalLast30d = aimSource ? aimSource.count : 0;
  // Simple heuristic: if axiom-aim has ever hit us but not in the last 24h,
  // show a warning. Uses last_24h count vs the same source presence.
  const feedSilent = aimTotalLast30d > 0 && (stats.last_24h ?? 0) === 0;

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4" data-testid="partner-analytics-widget">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-emerald-700" />
        <h3 className="text-sm font-bold text-zinc-900">Partner Diet Feed · Traffic</h3>
        <span className="text-[10px] text-zinc-500">/api/partner/diet</span>
        <button
          onClick={sendDigestNow}
          disabled={sendingDigest}
          className="ml-auto flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
          data-testid="send-digest-now-btn"
          title="Fire the daily digest email immediately (bypasses the 09:00 AEST schedule)"
        >
          <Mail className="w-3 h-3" />
          {sendingDigest ? 'Sending…' : 'Send digest now'}
        </button>
      </div>

      {feedSilent && (
        <div
          className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
          data-testid="feed-silent-warning"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Feed silent from axiom-aim.</strong> No hits from
            <code className="mx-1 font-mono">axiom-aim</code> in the last 24h.
            SuperAdmin will be emailed once per 24h until traffic resumes.
          </div>
        </div>
      )}

      {/* Headline stats */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <Stat label="Last 24h" value={stats.last_24h} testId="pdas-24h" />
        <Stat label="Last 7d" value={stats.last_7d} testId="pdas-7d" />
        <Stat label="Last 30d" value={stats.last_30d} testId="pdas-30d" />
        <Stat label="All time" value={stats.total} testId="pdas-total" />
      </div>

      {/* Mini bar chart: last 30 days */}
      <div className="mb-4">
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Last 30 days</p>
        {byDay.length === 0 ? (
          <p className="text-xs text-zinc-400">No hits yet in the last 30 days.</p>
        ) : (
          <div className="flex items-end gap-0.5 h-16" data-testid="pdas-by-day">
            {byDay.map(d => (
              <div
                key={d.date}
                className="flex-1 min-w-[3px] bg-emerald-500/80 hover:bg-emerald-600 rounded-t"
                style={{ height: `${(d.count / maxCount) * 100}%` }}
                title={`${d.date}: ${d.count} hits`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Top sources */}
      <div>
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Top sources (30d)</p>
        {(stats.by_source || []).length === 0 ? (
          <p className="text-xs text-zinc-400">No sources recorded yet.</p>
        ) : (
          <ul className="space-y-1" data-testid="pdas-by-source">
            {stats.by_source.map(s => (
              <li
                key={s.source}
                className="flex items-center justify-between text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-1"
              >
                <span className="font-mono text-zinc-700">{s.source}</span>
                <span className="font-semibold text-zinc-900">{s.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Integration handoff: download/copy the AIM developer doc */}
      <div className="mt-4 pt-3 border-t border-zinc-200" data-testid="pdas-integration-doc">
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-2">
          Integration handoff (for AIM devs)
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copyDoc}
            disabled={docBusy}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="copy-integration-doc-btn"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy doc
          </button>
          <button
            onClick={downloadDoc}
            disabled={docBusy}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="download-integration-doc-btn"
          >
            <FileText className="w-3.5 h-3.5" />
            Download .md
          </button>
        </div>
        <p className="text-[10px] text-zinc-500 mt-1.5">
          Send to the AIM team. Includes endpoint, schema, rate limits, ETag/If-Modified-Since caching, CORS origins and a React hook.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="border border-zinc-200 rounded p-2 text-center" data-testid={testId}>
      <div className="text-lg font-bold text-zinc-900">{(value ?? 0).toLocaleString()}</div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default PartnerDietAnalyticsWidget;
